import {
    AttachMoneyRounded as AttachMoneyRoundedIcon,
    Checklist as ChecklistIcon,
    DriveFileMoveOutlined as DriveFileMoveOutlinedIcon,
    MoneyOffRounded as MoneyOffRoundedIcon,
} from 'components/icons';
import SwitchMatterModal, { SwitchMatterModalResult } from 'components/modals/invoices/SwitchMatterModal.tsx';
import { useModal } from 'components/modals/useModal.ts';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util.tsx';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { useSwitchExpensesMatterMutation, useUpdateExpensesBillabilityMutation } from 'store/api/expenses.ts';
import { ExpenseListItem, ExpensesSwitchToMatterDto, UpdateExpensesBillability } from 'types/expenses.ts';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations.ts';
import { logError } from 'utils/monitoring';
import { useExpenseEntryDisabled } from 'utils/useExpenseEntryDisabled';
import ExpensesBulkSelectModal from './components/ExpensesBulkSelectModal.tsx';
import { isExpenseSelectable } from './ExpensesTable.tsx';

interface ExpensesTableListActionsProps {
    matterId?: number;
    isMatterNonBillable?: boolean;
    selectedIds: number[];
    onSelectedIdsChange: (selectedIds: number[]) => void;
    expenses: ExpenseListItem[];
}

const ExpensesTableActions = ({
    matterId,
    isMatterNonBillable = false,
    selectedIds,
    onSelectedIdsChange,
    expenses,
}: ExpensesTableListActionsProps) => {
    const isExpenseEntryDisabled = useExpenseEntryDisabled();
    const [switchExpensesMatter] = useSwitchExpensesMatterMutation();
    const [updateExpensesBillability, { isLoading: isBillabilityUpdating }] = useUpdateExpensesBillabilityMutation();
    const { openModal, closeModal } = useModal();

    const handleMatterSwitch = (value: SwitchMatterModalResult) => {
        if (!matterId || !value.matter?.id) {
            return;
        }

        const switchToMatterDto: ExpensesSwitchToMatterDto = {
            currentMatterId: matterId,
            newMatterId: value.matter?.id,
            newMatterAssignmentId: value?.matterAssignment?.id || null,
            expenseIds: selectedIds,
        };
        switchExpensesMatter(switchToMatterDto)
            .unwrap()
            .then(() => {
                successAlert(t('matters.expense-is-moved'));
            })
            .catch((err) => {
                console.error('Error switching matter:', err);
                logError(err);
            });
        closeModal();
    };

    const handleBillabilityChange = (isNonBillable: boolean) => {
        if (!matterId) {
            return;
        }

        const body: UpdateExpensesBillability = {
            matterId,
            expenseIds: selectedIds,
            isNonBillable,
        };
        updateExpensesBillability(body)
            .unwrap()
            .then(() => {
                onSelectedIdsChange([]);
                successAlert(
                    t(isNonBillable ? 'matters.expenses-marked-non-billable' : 'matters.expenses-marked-billable')
                );
            })
            .catch(logError);
    };

    const openSwitchMatterModal = (): void => {
        openModal(
            <SwitchMatterModal
                onSubmit={handleMatterSwitch}
                onCancel={closeModal}
                titleId={'invoices.move-to-another-case'}
                entityIds={new Set(selectedIds)}
                entitySingular={t('expenses.entity-name-singular')}
                entityPlural={t('expenses.entity-name-plural')}
                showAssignmentSelect={false}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const onBulkSelect = (isToSelect: boolean, ids: number[]) => {
        let newIds: number[];
        if (isToSelect) {
            const toAdd = ids.filter((id) => !selectedIds.includes(id));
            newIds = [...selectedIds, ...toAdd];
        } else {
            newIds = selectedIds.filter((id) => !ids.includes(id));
        }
        onSelectedIdsChange(newIds);
    };

    const handleBulkSelect = () => {
        openModal(
            <ExpensesBulkSelectModal
                onBulkSelect={(ids) => onBulkSelect(true, ids)}
                onBulkUnselect={(ids) => onBulkSelect(false, ids)}
                onClose={closeModal}
                expenses={expenses.map((expense) => ({
                    ...expense,
                    isSelectable: isExpenseSelectable(expense),
                }))}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const bulkSelectLabel = t('invoices.tooltips.bulk-selection');
    const markNonBillableLabel = t('matters.tooltips.mark-as-non-billable');
    const markBillableLabel = t('matters.tooltips.mark-as-billable');
    const moveLabel = isExpenseEntryDisabled
        ? t('expenses.integration-entry-disabled')
        : t('matters.tooltips.move-to-another-case');

    // The spans carry the tooltips: a disabled button fires no pointer events.
    return (
        <div className="flex justify-end gap-2">
            <Tooltip content={bulkSelectLabel} side="bottom">
                <Button variant="outline" size="icon-sm" aria-label={bulkSelectLabel} onClick={handleBulkSelect}>
                    <ChecklistIcon className="text-[20px]" />
                </Button>
            </Tooltip>
            <div className="flex">
                <Tooltip content={markNonBillableLabel} side="bottom">
                    <span className="inline-flex">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="rounded-r-none"
                            aria-label={markNonBillableLabel}
                            disabled={isBillabilityUpdating || !selectedIds.length}
                            onClick={() => handleBillabilityChange(true)}
                        >
                            <MoneyOffRoundedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip content={markBillableLabel} side="bottom">
                    <span className="inline-flex">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="-ml-px rounded-none"
                            aria-label={markBillableLabel}
                            disabled={isBillabilityUpdating || isMatterNonBillable || !selectedIds.length}
                            onClick={() => handleBillabilityChange(false)}
                        >
                            <AttachMoneyRoundedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip content={moveLabel} side="bottom">
                    <span className="inline-flex">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="-ml-px rounded-l-none"
                            aria-label={moveLabel}
                            disabled={!selectedIds.length || isExpenseEntryDisabled}
                            onClick={openSwitchMatterModal}
                        >
                            <DriveFileMoveOutlinedIcon className="text-[20px]" />
                        </Button>
                    </span>
                </Tooltip>
            </div>
        </div>
    );
};

export default ExpensesTableActions;
